import { SsaFunction, SsaBlock, SsaInstr, SsaValue, SsaVariable, SsaConst, COMPARE_INVERT, isVariable, isConst } from './SsaTypes';

export function optimizeSsa(ssaFunc: SsaFunction): void {
  stackFrameDetection(ssaFunc);
  let changed = true;
  let iteration = 0;
  while (changed && iteration < 10) {
    changed = false;
    changed = constantFolding(ssaFunc) || changed;
    changed = doubleNegationElimination(ssaFunc) || changed;
    changed = comparisonInversion(ssaFunc) || changed;
    changed = copyPropagation(ssaFunc) || changed;
    changed = localAssignInlining(ssaFunc) || changed;
    changed = localCommonSubexpressionElimination(ssaFunc) || changed;
    changed = deadCodeElimination(ssaFunc) || changed;
    changed = phiSimplification(ssaFunc) || changed;
    iteration++;
  }
}

/**
 * Detects the WASM stack frame prologue pattern: global_get(SP), const(N), sub, local_set, global_set(SP).
 * This is the standard shadow-stack protocol emitted by LLVM/Emscripten. The global_set that
 * saves the decremented stack pointer is removed since the epilogue will restore it.
 */
function stackFrameDetection(ssaFunc: SsaFunction): void {
  if (ssaFunc.blocks.length === 0) {
    return;
  }
  const entryBlock = ssaFunc.blocks.find(block => block.id === ssaFunc.entryBlockId);
  if (!entryBlock || entryBlock.instructions.length < 4) {
    return;
  }

  const instructions = entryBlock.instructions;
  let prologueEnd = -1;

  for (let index = 0; index + 3 < instructions.length; index++) {
    const i0 = instructions[index];
    const i1 = instructions[index + 1];
    const i2 = instructions[index + 2];
    const i3 = instructions[index + 3];

    if (i0.kind === 'global_get' &&
        i1.kind === 'const' && typeof i1.value === 'number' &&
        i2.kind === 'binary' && i2.op === '-' &&
        i3.kind === 'assign' && i3.result.name.startsWith('local')) {
      if (index + 4 < instructions.length) {
        const i4 = instructions[index + 4];
        if (i4.kind === 'global_set' && i4.globalIndex === i0.globalIndex) {
          prologueEnd = index + 5;
          break;
        }
      }
      prologueEnd = index + 4;
      break;
    }
  }

  if (prologueEnd > 0) {
    // Remove only the global_set (epilogue restores it), keep frame pointer computation
    const filtered: SsaInstr[] = [];
    for (let index = 0; index < prologueEnd; index++) {
      const instruction = instructions[index];
      if (instruction.kind !== 'global_set') {
        filtered.push(instruction);
      }
    }
    entryBlock.instructions.splice(0, prologueEnd, ...filtered);
  }
}

function getConstValue(value: SsaValue, defMap?: Map<number, { block: SsaBlock; instruction: SsaInstr }>): number | null {
  if (isConst(value)) {
    return Number(value.value);
  }
  if (isVariable(value) && defMap) {
    const def = defMap.get(value.id);
    if (def && def.instruction.kind === 'const') {
      return Number(def.instruction.value);
    }
  }
  return null;
}

/** Returns the raw constant value (number for i32/f*, bigint for i64) without lossy narrowing. */
function getRawConstValue(value: SsaValue, defMap?: Map<number, { block: SsaBlock; instruction: SsaInstr }>): number | bigint | null {
  if (isConst(value)) {
    return value.value;
  }
  if (isVariable(value) && defMap) {
    const def = defMap.get(value.id);
    if (def && def.instruction.kind === 'const') {
      return def.instruction.value;
    }
  }
  return null;
}

function isIntegerType(type: string): boolean {
  return type === 'i32' || type === 'i64';
}

type DefMap = Map<number, { block: SsaBlock; instruction: SsaInstr }>;

function buildDefMap(ssaFunc: SsaFunction): DefMap {
  const defMap = new Map<number, { block: SsaBlock; instruction: SsaInstr }>();
  for (const block of ssaFunc.blocks) {
    for (const instruction of block.instructions) {
      const result = getInstructionResult(instruction);
      if (result) {
        defMap.set(result.id, { block, instruction });
      }
    }
  }
  return defMap;
}

function getInstructionResult(instruction: SsaInstr): SsaVariable | null {
  switch (instruction.kind) {
    case 'phi':
    case 'assign':
    case 'const':
    case 'binary':
    case 'unary':
    case 'compare':
    case 'load':
    case 'convert':
    case 'select':
    case 'global_get':
      return instruction.result;
    case 'call':
    case 'call_indirect':
      return instruction.result || null;
    default:
      return null;
  }
}

function buildUseMap(ssaFunc: SsaFunction): Map<number, number> {
  const useCount = new Map<number, number>();

  function countUse(value: SsaValue): void {
    if (isVariable(value)) {
      useCount.set(value.id, (useCount.get(value.id) || 0) + 1);
    }
  }

  for (const block of ssaFunc.blocks) {
    for (const instruction of block.instructions) {
      visitInstructionOperands(instruction, countUse);
    }
  }
  return useCount;
}

/**
 * Applies `transform` to each operand value of an instruction, in place. This is
 * the single description of "what are an instruction's operands"; the read-only
 * visit and the operand replacement below are thin wrappers over it (a no-op
 * transform reads, an id-matching transform replaces).
 */
function mapOperands(instruction: SsaInstr, transform: (value: SsaValue) => SsaValue): void {
  switch (instruction.kind) {
    case 'phi':
      for (const input of instruction.inputs) { input.value = transform(input.value); }
      return;
    case 'assign':
    case 'global_set':
      instruction.value = transform(instruction.value);
      return;
    case 'binary':
    case 'compare':
      instruction.left = transform(instruction.left);
      instruction.right = transform(instruction.right);
      return;
    case 'unary':
    case 'convert':
      instruction.operand = transform(instruction.operand);
      return;
    case 'load':
      instruction.address = transform(instruction.address);
      return;
    case 'store':
      instruction.address = transform(instruction.address);
      instruction.value = transform(instruction.value);
      return;
    case 'call':
      for (let argIndex = 0; argIndex < instruction.args.length; argIndex++) { instruction.args[argIndex] = transform(instruction.args[argIndex]); }
      return;
    case 'call_indirect':
      instruction.tableIndex = transform(instruction.tableIndex);
      for (let argIndex = 0; argIndex < instruction.args.length; argIndex++) { instruction.args[argIndex] = transform(instruction.args[argIndex]); }
      return;
    case 'select':
      instruction.condition = transform(instruction.condition);
      instruction.trueVal = transform(instruction.trueVal);
      instruction.falseVal = transform(instruction.falseVal);
      return;
    case 'branch_if':
      instruction.condition = transform(instruction.condition);
      return;
    case 'branch_table':
      instruction.selector = transform(instruction.selector);
      return;
    case 'return':
      for (let returnIndex = 0; returnIndex < instruction.values.length; returnIndex++) { instruction.values[returnIndex] = transform(instruction.values[returnIndex]); }
      return;
  }
}

function visitInstructionOperands(instruction: SsaInstr, visitor: (value: SsaValue) => void): void {
  mapOperands(instruction, value => {
    visitor(value);
    return value;
  });
}

function replaceOperand(instruction: SsaInstr, oldId: number, newValue: SsaValue): boolean {
  let replaced = false;
  mapOperands(instruction, value => {
    if (isVariable(value) && value.id === oldId) {
      replaced = true;
      return newValue;
    }
    return value;
  });
  return replaced;
}

function doubleNegationElimination(ssaFunc: SsaFunction): boolean {
  let changed = false;
  const defMap = buildDefMap(ssaFunc);

  for (const block of ssaFunc.blocks) {
    for (let instrIndex = 0; instrIndex < block.instructions.length; instrIndex++) {
      const instruction = block.instructions[instrIndex];
      if (instruction.kind === 'unary' && instruction.op === '!' && isVariable(instruction.operand)) {
        const innerDef = defMap.get(instruction.operand.id);
        if (innerDef && innerDef.instruction.kind === 'unary' && innerDef.instruction.op === '!') {
          block.instructions[instrIndex] = {
            kind: 'assign',
            result: instruction.result,
            value: innerDef.instruction.operand,
          };
          changed = true;
        }
      }
    }
  }
  return changed;
}

function comparisonInversion(ssaFunc: SsaFunction): boolean {
  let changed = false;
  const defMap = buildDefMap(ssaFunc);

  for (const block of ssaFunc.blocks) {
    for (let instrIndex = 0; instrIndex < block.instructions.length; instrIndex++) {
      const instruction = block.instructions[instrIndex];
      if (instruction.kind === 'unary' && instruction.op === '!' && isVariable(instruction.operand)) {
        const innerDef = defMap.get(instruction.operand.id);
        if (innerDef && innerDef.instruction.kind === 'compare') {
          const inverted = COMPARE_INVERT[innerDef.instruction.op];
          if (inverted) {
            block.instructions[instrIndex] = {
              kind: 'compare',
              result: instruction.result,
              op: inverted,
              left: innerDef.instruction.left,
              right: innerDef.instruction.right,
            };
            changed = true;
          }
        }
      }
    }
  }
  return changed;
}

/**
 * Inlines local variable assignments to eliminate redundant temporaries.
 * When a local_X = assign(temp_Y) and temp_Y has exactly two uses (this assignment
 * and one other), the other use of temp_Y is replaced with local_X. This collapses
 * patterns like "tmp = expr; var = tmp; use(tmp)" into "var = expr; use(var)".
 */
function localAssignInlining(ssaFunc: SsaFunction): boolean {
  let changed = false;
  const useCount = buildUseMap(ssaFunc);

  for (const block of ssaFunc.blocks) {
    for (let assignIndex = 0; assignIndex < block.instructions.length; assignIndex++) {
      const instruction = block.instructions[assignIndex];
      if (instruction.kind !== 'assign' || !instruction.result.name.startsWith('local') || !isVariable(instruction.value)) {
        continue;
      }
      const sourceVar = instruction.value;
      if ((useCount.get(sourceVar.id) || 0) !== 2) {
        continue;
      }
      // Rewrite the other use of the temp to read the local instead — but only
      // when that use appears after the assign in the same block. Rewriting an
      // earlier or cross-block use would read the local before it is defined.
      for (let useIndex = assignIndex + 1; useIndex < block.instructions.length; useIndex++) {
        if (replaceOperand(block.instructions[useIndex], sourceVar.id, instruction.result)) {
          changed = true;
        }
      }
    }
  }
  return changed;
}

function constantFolding(ssaFunc: SsaFunction): boolean {
  let changed = false;
  const defMap = buildDefMap(ssaFunc);
  for (const block of ssaFunc.blocks) {
    for (let instrIndex = 0; instrIndex < block.instructions.length; instrIndex++) {
      const folded = foldInstruction(block.instructions[instrIndex], defMap);
      if (folded) {
        block.instructions[instrIndex] = folded;
        changed = true;
      }
    }
  }
  return changed;
}

// Folding and algebraic simplification only apply to integer arithmetic and
// comparisons. Float ops (x*0, x+0, truncation) and i64 precision must not be
// folded with i32 semantics, so non-integer binaries are left untouched.
function foldInstruction(instruction: SsaInstr, defMap: DefMap): SsaInstr | null {
  if (instruction.kind === 'binary' && isIntegerType(instruction.result.type)) {
    return foldIntegerBinary(instruction, defMap);
  }
  if (instruction.kind === 'compare') {
    return foldComparison(instruction, defMap);
  }
  if (instruction.kind === 'unary' && instruction.op === '!') {
    return foldLogicalNot(instruction, defMap);
  }
  return null;
}

function foldIntegerBinary(instruction: SsaInstr & { kind: 'binary' }, defMap: DefMap): SsaInstr | null {
  const resultType = instruction.result.type;
  const isI64 = resultType === 'i64';
  const leftConst = getRawConstValue(instruction.left, defMap);
  const rightConst = getRawConstValue(instruction.right, defMap);
  if (leftConst !== null && rightConst !== null) {
    const folded = isI64
      ? evaluateI64BinaryOp(instruction.op, BigInt(leftConst), BigInt(rightConst))
      : evaluateBinaryOp(instruction.op, Number(leftConst), Number(rightConst));
    if (folded === null) {
      return null;
    }
    return { kind: 'const', result: instruction.result, value: folded, type: resultType };
  }
  return foldBinaryIdentity(instruction, resultType, isI64, rightConst);
}

function foldBinaryIdentity(instruction: SsaInstr & { kind: 'binary' }, resultType: string, isI64: boolean, rightConst: number | bigint | null): SsaInstr | null {
  const rightNumber = rightConst === null ? null : Number(rightConst);
  if (rightNumber === 0 && (instruction.op === '+' || instruction.op === '-' || instruction.op === '|' || instruction.op === '^')) {
    return { kind: 'assign', result: instruction.result, value: instruction.left };
  }
  if (rightNumber !== null && rightNumber < 0 && instruction.op === '+') {
    // x + (-N) → x - N
    const negated: number | bigint = isI64 ? -BigInt(rightConst!) : -rightNumber;
    const positiveConst: SsaValue = { kind: 'const', value: negated, type: resultType };
    return { kind: 'binary', result: instruction.result, op: '-', left: instruction.left, right: positiveConst };
  }
  if (rightNumber === 1 && instruction.op === '*') {
    return { kind: 'assign', result: instruction.result, value: instruction.left };
  }
  if (rightNumber === 0 && instruction.op === '*') {
    return { kind: 'const', result: instruction.result, value: isI64 ? 0n : 0, type: resultType };
  }
  return null;
}

function foldComparison(instruction: SsaInstr & { kind: 'compare' }, defMap: DefMap): SsaInstr | null {
  const leftConst = getConstValue(instruction.left, defMap);
  const rightConst = getConstValue(instruction.right, defMap);
  if (leftConst !== null && rightConst !== null) {
    const result = evaluateCompareOp(instruction.op, leftConst, rightConst);
    if (result !== null) {
      return { kind: 'const', result: instruction.result, value: result ? 1 : 0, type: 'i32' };
    }
  }
  return null;
}

function foldLogicalNot(instruction: SsaInstr & { kind: 'unary' }, defMap: DefMap): SsaInstr | null {
  const constVal = getConstValue(instruction.operand, defMap);
  if (constVal !== null) {
    return { kind: 'const', result: instruction.result, value: constVal === 0 ? 1 : 0, type: 'i32' };
  }
  return null;
}

function evaluateBinaryOp(op: string, left: number, right: number): number | null {
  switch (op) {
    case '+': return (left + right) | 0;
    case '-': return (left - right) | 0;
    case '*': return Math.imul(left, right);
    case '&': return left & right;
    case '|': return left | right;
    case '^': return left ^ right;
    case '<<': return left << (right & 31);
    case '>>': return left >> (right & 31);
    case '>>>': return left >>> (right & 31);
    // i32.div_s overflow (INT_MIN / -1) traps in wasm — do not fold to a value.
    case '/': return right !== 0 && !(left === -2147483648 && right === -1) ? (left / right) | 0 : null;
    case '%': return right !== 0 ? (left % right) | 0 : null;
    case '/u': return right !== 0 ? ((left >>> 0) / (right >>> 0)) | 0 : null;
    case '%u': return right !== 0 ? ((left >>> 0) % (right >>> 0)) | 0 : null;
    default: return null;
  }
}

const MIN_I64 = -(2n ** 63n);

function evaluateI64BinaryOp(op: string, left: bigint, right: bigint): bigint | null {
  const wrap = (value: bigint): bigint => BigInt.asIntN(64, value);
  switch (op) {
    case '+': return wrap(left + right);
    case '-': return wrap(left - right);
    case '*': return wrap(left * right);
    case '&': return wrap(left & right);
    case '|': return wrap(left | right);
    case '^': return wrap(left ^ right);
    case '<<': return wrap(left << (right & 63n));
    case '>>': return wrap(left >> (right & 63n));
    case '>>>': return wrap(BigInt.asUintN(64, left) >> (right & 63n));
    // i64.div_s overflow (INT64_MIN / -1) traps in wasm — do not fold.
    case '/': return right !== 0n && !(left === MIN_I64 && right === -1n) ? wrap(left / right) : null;
    case '%': return right !== 0n ? wrap(left % right) : null;
    case '/u': return right !== 0n ? wrap(BigInt.asUintN(64, left) / BigInt.asUintN(64, right)) : null;
    case '%u': return right !== 0n ? wrap(BigInt.asUintN(64, left) % BigInt.asUintN(64, right)) : null;
    default: return null;
  }
}

function evaluateCompareOp(op: string, left: number, right: number): boolean | null {
  switch (op) {
    case '==': return left === right;
    case '!=': return left !== right;
    case '<': return left < right;
    case '>': return left > right;
    case '<=': return left <= right;
    case '>=': return left >= right;
    default: return null;
  }
}

/**
 * Propagates copy assignments (x = y) by replacing all uses of x with y.
 * Local and phi variable assignments are excluded to preserve the user-meaningful
 * names that local variables carry through the lowering pipeline.
 */
function copyPropagation(ssaFunc: SsaFunction): boolean {
  let changed = false;

  for (const block of ssaFunc.blocks) {
    for (const instruction of block.instructions) {
      if (instruction.kind === 'assign' && isVariable(instruction.value)) {
        // Don't propagate local/phi assigns backwards — we want to keep the local name
        if (instruction.result.name.startsWith('local') || instruction.result.name.startsWith('phi')) {
          continue;
        }
        const targetId = instruction.result.id;

        for (const otherBlock of ssaFunc.blocks) {
          for (const otherInstruction of otherBlock.instructions) {
            if (otherInstruction === instruction) {
              continue;
            }
            if (replaceOperand(otherInstruction, targetId, instruction.value)) {
              changed = true;
            }
          }
        }
      }
    }
  }
  return changed;
}

function deadCodeElimination(ssaFunc: SsaFunction): boolean {
  let changed = false;
  const useCount = buildUseMap(ssaFunc);

  for (const block of ssaFunc.blocks) {
    const filtered: SsaInstr[] = [];
    for (const instruction of block.instructions) {
      const result = getInstructionResult(instruction);
      if (result && (useCount.get(result.id) || 0) === 0) {
        if (instruction.kind !== 'call' && instruction.kind !== 'call_indirect' &&
            instruction.kind !== 'store' && instruction.kind !== 'global_set' &&
            instruction.kind !== 'phi') {
          // Don't DCE assigns to local variables — they represent side effects
          // that flow through phi nodes to other blocks
          if (instruction.kind === 'assign' && result.name.startsWith('local')) {
            // keep it
          } else {
            changed = true;
            continue;
          }
        }
      }
      filtered.push(instruction);
    }
    if (filtered.length !== block.instructions.length) {
      block.instructions = filtered;
    }
  }
  return changed;
}

function phiSimplification(ssaFunc: SsaFunction): boolean {
  let changed = false;

  for (const block of ssaFunc.blocks) {
    for (let instrIndex = 0; instrIndex < block.instructions.length; instrIndex++) {
      const instruction = block.instructions[instrIndex];
      if (instruction.kind !== 'phi') {
        continue;
      }

      const uniqueInputs = new Map<number | string, SsaValue>();
      for (const input of instruction.inputs) {
        const key = isVariable(input.value) ? input.value.id : `const_${(input.value as SsaConst).value}`;
        if (!uniqueInputs.has(key)) {
          uniqueInputs.set(key, input.value);
        }
      }

      const filteredInputs = Array.from(uniqueInputs.values())
        .filter(value => !(isVariable(value) && value.id === instruction.result.id));

      if (filteredInputs.length === 1) {
        block.instructions[instrIndex] = {
          kind: 'assign',
          result: instruction.result,
          value: filteredInputs[0],
        };
        changed = true;
      }
    }
  }
  return changed;
}

function localCommonSubexpressionElimination(ssaFunc: SsaFunction): boolean {
  let changed = false;

  for (const block of ssaFunc.blocks) {
    const loadCache = new Map<string, SsaVariable>();

    for (let instrIndex = 0; instrIndex < block.instructions.length; instrIndex++) {
      const instruction = block.instructions[instrIndex];

      if (instruction.kind === 'store' || instruction.kind === 'call' || instruction.kind === 'call_indirect') {
        loadCache.clear();
        continue;
      }

      if (instruction.kind === 'load' && isVariable(instruction.address)) {
        const key = `${instruction.address.id}:${instruction.offset}:${instruction.loadType}`;
        const existing = loadCache.get(key);
        if (existing) {
          block.instructions[instrIndex] = {
            kind: 'assign',
            result: instruction.result,
            value: existing,
          };
          changed = true;
        } else {
          loadCache.set(key, instruction.result);
        }
      }
    }
  }
  return changed;
}
